import { z } from 'zod';

/**
 * Reference from backend implementation
 * https://bitbucket.org/atlassian/collaboration-graph/src/1ef98842b47fd03cb263559e1e89f5e1ffdb8009/src/main/java/com/atlassian/smarts/recommendations/predictions/userdoc/UserDocPredictionService.java#lines-45:49
 */
export enum RecommendedEventType {
	CLICKED = 'clicked',
	COMMENTED = 'commented',
	EDITED = 'edited',
	VIEWED = 'viewed',
	WERE_MENTIONED = 'were mentioned',
}

export enum RecommendedType {
	COLLABORATOR = 'collaborator',
	NO_COLLABORATOR = 'no_collaborator',
}

const RecommendedReasonMetadataSchema: z.ZodObject<
	{
		collaborators: z.ZodOptional<z.ZodArray<z.ZodString, 'many'>>;
	},
	'strip'
> = z.object({
	collaborators: z.array(z.string()).optional(),
});

export type RecommendedReasonMetadata = z.infer<typeof RecommendedReasonMetadataSchema>;

const RecommendedReasonSchema: z.ZodObject<
	{
		type: z.ZodNativeEnum<typeof RecommendedType>;
		timestamp: z.ZodString;
		text: z.ZodString;
		feature: z.ZodOptional<z.ZodString>;
		metadata: z.ZodOptional<
			z.ZodObject<
				{
					collaborators: z.ZodOptional<z.ZodArray<z.ZodString, 'many'>>;
				},
				'strip',
				z.ZodTypeAny,
				{
					collaborators?: string[] | undefined;
				},
				{
					collaborators?: string[] | undefined;
				}
			>
		>;
	},
	'strip'
> = z.object({
	type: z.nativeEnum(RecommendedType),
	timestamp: z.string().datetime(),
	text: z.string(),
	feature: z.string().optional(),
	metadata: RecommendedReasonMetadataSchema.optional(),
});

export type RecommendedReason = z.infer<typeof RecommendedReasonSchema>;

export const parseRecommendedReason = (
	reason: string | undefined,
	logException: (error: Error) => Promise<void>,
): RecommendedReason | null => {
	try {
		const result = RecommendedReasonSchema.safeParse(JSON.parse(reason || '{}'));
		if (result.success) {
			return result.data;
		}
		logException(result.error);
		return null;
	} catch (error) {
		logException(error as Error);
		return null;
	}
};
